<template>
  <div class="space-y-5">
    <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <ComponentHeader
        title="Data Source"
        subtitle="DataAttribute steps, joins, identifiers, and composed output"
        :icon="['fas', 'layer-group']"
        :heading-level="3"
      >
        <template #actions>
          <span class="text-[11px]" :class="precomputeError ? 'text-red-600' : precomputeMessage ? 'text-emerald-600' : 'text-gray-400'">
            {{ precomputeError || precomputeMessage }}
          </span>
          <button class="btn-accent-sm whitespace-nowrap" :disabled="precomputing" @click="precompute">
            {{ precomputing ? 'Precomputing…' : 'Precompute' }}
          </button>
        </template>
      </ComponentHeader>
      <div class="p-4 bg-gray-50">
        <DataSourceSection
          :record="localRecord"
          :artifact="artifact"
          :context="context"
          @patch="onPatch"
          @refresh="$emit('refresh')"
        />
      </div>
    </section>

    <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <ComponentHeader
        title="Calculation"
        subtitle="Calculation grain, aggregation type, primary keys, and numerator conditions"
        :icon="['fas', 'chart-bar']"
        :heading-level="3"
      />
      <div class="p-5">
        <AggregationSection
          :record="localRecord"
          :artifact="artifact"
          :context="context"
          @patch="onPatch"
        />
      </div>
    </section>

    <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <ComponentHeader
        title="Display"
        subtitle="Number format, direction, runout marker, and thresholds"
        :icon="['fas', 'pen-to-square']"
        :heading-level="3"
      />
      <div class="p-5">
        <DisplaySection
          :record="localRecord"
          :artifact="artifact"
          :context="context"
          @patch="onPatch"
        />
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import DataSourceSection from './DataSourceSection.vue';
import AggregationSection from './AggregationSection.vue';
import DisplaySection from './DisplaySection.vue';

export default {
  name: 'MetricConfigSection',
  components: {
    ComponentHeader,
    DataSourceSection,
    AggregationSection,
    DisplaySection,
  },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch', 'refresh'],
  data() {
    return {
      localRecord: this.clone(this.record),
      precomputing: false,
      precomputeMessage: '',
      precomputeError: '',
    };
  },
  watch: {
    record: {
      deep: true,
      handler(value) {
        this.localRecord = this.clone(value);
      },
    },
  },
  methods: {
    async precompute() {
      this.precomputing = true;
      this.precomputeMessage = '';
      this.precomputeError = '';
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.localRecord.id}/precompute-data-source-v2/`,
          { data_source_v2: (this.localRecord.composable_config || {}).data_source_v2 || {} },
          { timeout: 600000 },
        );
        this.precomputeMessage = `${Number(data.aggregate_rows || 0).toLocaleString()} aggregate rows`;
      } catch (error) {
        this.precomputeError = error?.response?.data?.error || error.message;
      } finally {
        this.precomputing = false;
      }
    },
    clone(value) {
      return JSON.parse(JSON.stringify(value || {}));
    },
    onPatch(patch) {
      const next = { ...this.localRecord, ...patch };
      if (patch.composable_config) {
        const currentConfig = this.localRecord.composable_config || {};
        const patchConfig = patch.composable_config || {};
        next.composable_config = {
          ...currentConfig,
          ...patchConfig,
          studio_config: {
            ...(currentConfig.studio_config || {}),
            ...(patchConfig.studio_config || {}),
          },
          data_source_v2: {
            ...(currentConfig.data_source_v2 || {}),
            ...(patchConfig.data_source_v2 || {}),
          },
        };
        patch = { ...patch, composable_config: next.composable_config };
      }
      if (patch.display_config) {
        next.display_config = {
          ...(this.localRecord.display_config || {}),
          ...patch.display_config,
        };
        patch = { ...patch, display_config: next.display_config };
      }
      this.localRecord = next;
      this.$emit('patch', patch);
    },
  },
};
</script>
